import React, { ComponentType, ReactNode, useMemo } from 'react'
import classNames from 'classnames'
import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Divider } from '../../components/Divider/Divider'

export interface Option {
  className?: string
  selectOption: SelectOption
  icon?: string
  handleSelected: (option: SelectOption) => void | Promise<void>
  IconComponent?: ComponentType
  children?: ReactNode
  triggerOverlay?: boolean
  tooltipLabel?: string
}

export interface AdditionalOptionsButtonProps {
  resultOptions: Option[]
  headerComponent?: ComponentType
  buttonLabel?: string
  disabled: boolean
  loading: boolean
}

export const AdditionalOptionsButton: React.FC<AdditionalOptionsButtonProps> = ({ resultOptions, headerComponent, buttonLabel, disabled, loading }) => {
  const options = useMemo(() => resultOptions.map(({ selectOption }) => selectOption), [resultOptions])

  const handleOptionSelected = async (option: SelectOption) => {
    const { id: resultOptionId } = option
    const resultOption = resultOptions.find((resultOption) => resultOption.selectOption.id === resultOptionId)
    if (!resultOption) {
      return
    }
    const { handleSelected } = resultOption
    await handleSelected(option)
  }

  const AdditionalOptionsDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({ optionId, displayLabel, disabled, onClick }) => {
    const resultOption = resultOptions.find((resultOption) => resultOption.selectOption.id === optionId)
    if (!resultOption) {
      return null
    }
    const { icon, className, IconComponent, triggerOverlay, tooltipLabel } = resultOption
    const optionBody =
      className === 'group-divider' ? (
        <Divider className='my-2' />
      ) : (
        <CustomDropdownItemOption
          role='button'
          onClick={onClick}
          className={classNames('d-flex gap-2 align-items-center p-2', className, { 'dropdown-item-disabled': disabled })}
        >
          {IconComponent ? <IconComponent /> : icon ? <i className={`bi ${icon} fs-6`} /> : null}
          {displayLabel}
        </CustomDropdownItemOption>
      )
    return (
      <OverlayTrigger trigger={triggerOverlay ? ['hover', 'focus'] : []} overlay={<Tooltip id={`${optionId}-tooltip`}>{tooltipLabel}</Tooltip>}>
        {optionBody}
      </OverlayTrigger>
    )
  }

  return (
    <SelectDropdown
      className='zocks-py-0.375 px-2'
      options={options}
      icon={buttonLabel ? undefined : 'bi-three-dots-vertical'}
      initialSelectedOption={buttonLabel ? { id: '', label: buttonLabel } : null}
      iconOnly={!buttonLabel}
      hideArrows
      onOptionSelected={handleOptionSelected}
      DropDownItemOptionComponent={AdditionalOptionsDropdownItemOption}
      DropdownHeaderComponent={headerComponent}
      disabled={disabled}
      loading={loading}
    />
  )
}
